/**
 * Parental gate (Families Policy): an addition a child aged 2-6 cannot
 * solve. No text — lock + operation + large numeric keypad.
 * Regenerates the operation on failure.
 */

import { createSignal, For, Show } from "solid-js";
import { Lock, X } from "@fleet/icons";
import { cn } from "@/lib/cn";
import { useBackDismiss } from "@/lib/use-back-dismiss";
import { useAppConfig } from "@/providers/config-provider";

interface ParentalGateProps {
  open: boolean;
  onSuccess: () => void;
  onDismiss: () => void;
}

function randomChallenge(min: number, span: number) {
  const a = min + Math.floor(Math.random() * span);
  const b = min + Math.floor(Math.random() * span);
  return { a, b, answer: a + b };
}

const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

export function ParentalGate(props: ParentalGateProps) {
  // The addition difficulty is configurable by platform: 3+6 is enough for
  // a 3-year-old child, but not for a 6-year-old who can already add. It does not
  // rise to the adult level on purpose — whoever hardens it is whoever publishes
  // the binary.
  const { parental } = useAppConfig().config();
  const newChallenge = () => randomChallenge(parental.challengeMin, parental.challengeSpan);
  const [challenge, setChallenge] = createSignal(newChallenge());
  const [input, setInput] = createSignal("");
  const [shake, setShake] = createSignal(false);

  // The browser's "Back" DISCARDS the gate (return to the child's safe zone).
  // It only wires close/dismiss — never `onSuccess`, so the security control
  // cannot be bypassed to reach the adult zone.
  useBackDismiss(
    () => props.open,
    () => {
      setInput("");
      props.onDismiss();
    },
  );

  const submit = (value: string) => {
    if (Number.parseInt(value, 10) === challenge().answer) {
      setInput("");
      props.onSuccess();
      return;
    }
    // Failure: new operation and shake feedback, no text.
    setChallenge(newChallenge());
    setInput("");
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  const pressDigit = (digit: number) => {
    const next = `${input()}${digit}`;
    setInput(next);
    if (next.length >= String(challenge().answer).length) {
      submit(next);
    }
  };

  return (
    <Show when={props.open}>
      <div
        // `overflow-y-auto` + `items-start` with automatic vertical margin: the dialog
        // is centered when it fits, but if the viewport is very short (landscape on
        // a small phone) the overlay scrolls instead of cropping the keyboard and
        // leaving digits unreachable. Never `overflow:hidden` here.
        class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
        aria-label="Parental gate"
      >
        <div
          class={cn(
            "my-auto w-full max-w-sm rounded-[2rem] bg-surface p-[clamp(1rem,4vw,1.5rem)] shadow-2xl",
            shake() && "animate-[shake_0.4s_ease-in-out]",
          )}
        >
          <div class="flex items-center justify-between pb-4">
            <span class="flex h-11 w-11 items-center justify-center rounded-full bg-default">
              <Lock class="h-5 w-5 text-muted" aria-hidden="true" />
            </span>
            <button
              type="button"
              onClick={() => {
                setInput("");
                props.onDismiss();
              }}
              class="flex h-12 w-12 items-center justify-center rounded-full text-muted hover:bg-default active:scale-95"
              aria-label="Close"
            >
              <X class="h-6 w-6" />
            </button>
          </div>

          <p class="font-display pb-[clamp(0.75rem,2vh,1.25rem)] text-center text-[clamp(1.75rem,6vw,2.25rem)] tracking-wider text-foreground">
            {challenge().a} + {challenge().b} = {input() || "?"}
          </p>

          <div class="grid grid-cols-3 gap-[clamp(0.5rem,1.5vw,0.75rem)]">
            <For each={DIGITS}>
              {(digit) => (
                <button
                  type="button"
                  onClick={() => {
                    pressDigit(digit);
                  }}
                  class={cn(
                    // Fluid height but ALWAYS ≥56px (large touch target for little
                    // hands) and bounded to fit in low landscape.
                    "tile-3d tile-3d-press font-display flex h-[clamp(3.5rem,9vh,5rem)] items-center justify-center rounded-2xl bg-default text-[clamp(1.5rem,4vw,1.875rem)] text-foreground",
                    digit === 0 && "col-start-2",
                  )}
                >
                  {digit}
                </button>
              )}
            </For>
          </div>
        </div>
      </div>
    </Show>
  );
}
